import { selectMiniTrendShapes } from "./miniChartSelectors";
// src/app/components/RoomColoringTrendChart/RoomColoringTrendChart.tsx
import React from "react";
import { Box, Text } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { AgCharts } from "ag-charts-react";

import { useAppSelector } from "../app/hooks";
import { skipToken } from "@reduxjs/toolkit/query";
import { usePostMultiplacerRoomSnapshotTrendByKeyQuery } from "../../openapi";
import { selectRoomColoringTrendChart } from "./roomColoringTrendSelectors";
import { selectTrendByKeyArgsForRoomColoring } from "./roomColoringSelectors";

const TREND_REQUEST_DEBOUNCE_MS = 500;

export const RoomColoringTrendChart: React.FC<{ compact?: boolean }> = ({ compact = false }) => {
  const args = useAppSelector(selectTrendByKeyArgsForRoomColoring);
  const [debouncedArgs] = useDebouncedValue(args, TREND_REQUEST_DEBOUNCE_MS);

  // ✅ switched from mcbim -> multiplacer roomSnapshots
  const { isLoading, isError, error } = usePostMultiplacerRoomSnapshotTrendByKeyQuery(
    debouncedArgs ?? skipToken
  );

  const chart = useAppSelector(selectRoomColoringTrendChart);
  const { options, isEmpty } = chart;
  const miniShapes = selectMiniTrendShapes(chart);

  if (compact && (isEmpty || isLoading || isError)) return null;

  if (isEmpty)
    return (
      <Text size="sm" c="dimmed">
        Ingen data for valgt filter.
      </Text>
    );

  if (isLoading) {
    return (
      <Box>
        <Text size="sm" c="dimmed">
          Laster trend…
        </Text>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box>
        <Text size="sm" c="red">
          Klarte ikke å laste trend.
        </Text>
        <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{JSON.stringify(error, null, 2)}</pre>
      </Box>
    );
  }

  if (compact) {
    return (
      <svg width="80" height="30" viewBox="0 0 80 30" aria-hidden="true" focusable="false">
        {miniShapes.map((shape) =>
          shape.single ? (
            <rect
              key={shape.key}
              x="0"
              y={shape.y}
              width="80"
              height={shape.height}
              fill={shape.fill}
            />
          ) : (
            <polygon key={shape.key} points={shape.points} fill={shape.fill} />
          )
        )}
      </svg>
    );
  }

  return (
    <Box>
      <AgCharts options={options} />
    </Box>
  );
};
